import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import safeStyle from 'webapp/helpers/safe-style';
import AccountProgressStepComponent from 'webapp/components/account-progress/step';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type { Stage } from 'webapp/services/account-progress';

interface Args {
	model: Stage | null;
	inPopup?: boolean;
	inPanel?: boolean;
	onContentChange?: () => void;
}

export default class AccountProgressStageComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;

	get model() {
		return this.args.model;
	}

	get stageId() {
		return this.model?.id;
	}

	get stageWithId() {
		return `stage-${this.stageId}`;
	}

	get inPopup() {
		return !!this.args.inPopup;
	}

	get inPanel() {
		return !!this.args.inPanel;
	}

	get inPage() {
		return !this.inPopup && !this.inPanel;
	}

	get showVideoStep() {
		return (
			this.stageId === 1 &&
			!this.accountService.isClient &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
		);
	}

	get showMoreDetails() {
		return (
			this.accountProgressService.canSeeAccountProgressSubpage &&
			!this.navigationService.activatedRoute.includes('settings.level')
		);
	}

	get isCompleted() {
		const stageId = this.stageId;
		return stageId ? this.accountProgressService.stage > stageId : false;
	}

	get isActive() {
		return (
			!!this.accountProgressService.activeStage &&
			this.accountProgressService.activeStage.id === this.stageId
		);
	}

	get isLastCompleted() {
		const stageId = this.stageId,
			isCurrentStage = stageId ? stageId + 1 === this.accountProgressService.stage : false;

		return this.isCompleted && isCurrentStage;
	}

	get nextStage() {
		const stageId = this.stageId;
		return stageId
			? this.accountProgressService.stagesData.find((item) => item.id === stageId + 1)
			: null;
	}

	get percentDone() {
		return this.accountProgressService.pointsWithPercentage;
	}

	get stepCount() {
		let count = (this.model?.steps || []).length;
		if (this.showVideoStep) {
			count++;
		}
		return count;
	}

	get trackOrigin() {
		let origin = 'progress_page';

		if (this.inPopup) {
			origin = 'popup';
		} else if (this.inPanel) {
			origin = 'panel';
		}

		return origin;
	}

	get showMoreDetailsInPopup() {
		return this.inPopup && this.showMoreDetails;
	}

	@action
	showVideo(e: PointerEvent) {
		e.preventDefault();
		this.navigationService.openIntroTourVideoPopup(false, false, 'account-progress');
	}

	@action
	onContentChange() {
		if (this.args.onContentChange) {
			this.args.onContentChange();
		}
	}
	<template>
		<div
			class="stage flex-item
				{{this.stageWithId}}
				{{if this.isCompleted 'done'}}
				{{if this.isActive 'active'}}
				{{if this.accountService.isClient 'client'}}"
		>

			<div class="overview">
				<CommonUiIconComponent @name="Badge" @size="2xl" />
				<div class="t">{{@model.name}}</div>
				{{#if this.isCompleted}}
					<div class="l">Level {{@model.id}} completed</div>
					<div class="check"><CommonUiIconComponent @name="Check" @size="sm" /></div>
				{{else}}
					<div class="l">Level {{@model.id}}</div>
					{{#if this.isActive}}
						<div class="bar">
							<span style={{safeStyle this.percentDone property="width"}}></span>
						</div>
						<div class="p">Setup progress: <em>{{this.percentDone}}</em></div>
					{{/if}}
				{{/if}}
			</div>

			<div class="steps">
				{{#if this.inPage}}
					<strong>Steps to be completed to reach this level:</strong>
				{{else if this.inPanel}}
					<strong>
						<em>{{this.currentUserService.model.firstName}}</em>, follow these
						{{this.stepCount}}
						steps to get you up and running:
					</strong>
				{{/if}}

				<ul>
					{{#if this.showVideoStep}}
						<li class="step-video">
							<div class="step-ct">
								<a role="button" class="title" {{on "click" this.showVideo}}>
									<CommonUiIconComponent
										@name="Play"
										@size="xs"
										@class="step-play"
									/>
									Watch 2-min intro video
								</a>
							</div>
						</li>
					{{/if}}
					{{#each @model.steps as |step|}}
						<AccountProgressStepComponent
							@model={{step}}
							@stage={{@model}}
							@origin={{this.trackOrigin}}
							@onContentChange={{this.onContentChange}}
						/>
					{{/each}}
				</ul>

				{{#if this.showMoreDetailsInPopup}}
					<hr />
					<LinkTo class="more" @route="settings.level">More details</LinkTo>
				{{/if}}
			</div>

		</div>
	</template>
}
